import * as React from "react";
import Card from "web_modules/loan_officer_dashboard/shared/Card";
import {
  Address,
  dateStringAsMoment,
  BorrowerInformation,
  DateAsString,
  DecimalAsString,
  FormatAddress,
  formatRateAsPercent,
  TimeAsString,
  CreditScore,
  FICOScore,
} from "web_modules/shared/mortgage_definitions";
import Money from "web_modules/Money";
import { FormattedDate } from "react-intl";
import { Link } from "react-router-dom";
import { LoanStatus, loanStatusToStr } from "../../shared/loan_statuses";

const styles = require("./styles.module.scss");

const ensureArray = obj => (Array.isArray(obj) ? obj : [obj]);

const DataRow: React.FunctionComponent<{
  title: string;
  children: React.ReactNode[] | React.ReactNode;
}> = ({ title, children }) => (
  <tr>
    <td className={styles.rowTitle}>
      <strong>{title}:</strong>
    </td>
    {ensureArray(children).map((child, index) => (
      <td key={`${title}_${index}`}>{child}</td>
    ))}
  </tr>
);

interface HeaderProps {
  externalLoanId: string;
  borrower1FullName: string;
  borrower2FullName?: string;
  loanStatus: LoanStatus | "–";
  property: Address;
}

export const Header: React.FunctionComponent<HeaderProps> = ({
  externalLoanId,
  borrower1FullName,
  borrower2FullName = null,
  loanStatus,
  property,
}) => (
  <Card className={styles.card}>
    <table>
      <tbody>
        <DataRow title="External Loan ID">{externalLoanId}</DataRow>
        <DataRow title="Borrowers">
          {`${borrower1FullName}${borrower2FullName ? `, and ${borrower2FullName}` : ""}`}
        </DataRow>
        <DataRow title="Property">{FormatAddress(property) || "None"}</DataRow>
        <DataRow title="Loan Status">{loanStatusToStr(loanStatus)}</DataRow>
      </tbody>
    </table>
  </Card>
);

interface BorrowerInfoProps {
  borrower1: BorrowerInformation;
  borrower2?: BorrowerInformation;
  borrower1CreditScores?: CreditScore[];
  borrower2CreditScores?: CreditScore[];
}

export const BorrowerInfo: React.FunctionComponent<BorrowerInfoProps> = ({
  borrower1,
  borrower2 = null,
  borrower1CreditScores = [],
  borrower2CreditScores = [],
}) => (
  <Card className={styles.card}>
    <table>
      <thead>
        <tr>
          <td />
          <td>Borrower 1</td>
          {borrower2 && <td>Borrower 2</td>}
        </tr>
      </thead>
      <tbody>
        <DataRow title="First Name">
          {borrower1.FirstName}
          {borrower2 && borrower2.FirstName}
        </DataRow>
        <DataRow title="Middle Name">
          {borrower1.MiddleName}
          {borrower2 && borrower2.MiddleName}
        </DataRow>
        <DataRow title="Last Name">
          {borrower1.LastName}
          {borrower2 && borrower2.LastName}
        </DataRow>
        <DataRow title="Suffix">
          {borrower1.Suffix}
          {borrower2 && borrower2.Suffix}
        </DataRow>
        <DataRow title="Age">
          {`${dateStringAsMoment(borrower1.DOB).toNow(true)} old`}
          {borrower2 && `${dateStringAsMoment(borrower2.DOB).toNow(true)} old`}
        </DataRow>
        <DataRow title="Marital Status">
          {borrower1.MaritalStatus}
          {borrower2 && borrower2.MaritalStatus}
        </DataRow>
        <DataRow title="FICO Score">
          {`${FICOScore(borrower1CreditScores) || "None"}`}
          {borrower2 && `${FICOScore(borrower2CreditScores) || "None"}`}
        </DataRow>
        <DataRow title="Address">
          {FormatAddress(borrower1.PresentAddress.Address) || "None"}
          {(borrower2 && FormatAddress(borrower2.PresentAddress.Address)) || "None"}
        </DataRow>
      </tbody>
    </table>
  </Card>
);

interface TransactionDetailsProps {
  loanPurpose: string;
  occupancy?: string;
  propertyType?: string;
  closingDate?: DateAsString;
  loanAmount: DecimalAsString;
  purchasePrice: DecimalAsString;
  LTV: DecimalAsString;
  interestRate: DecimalAsString;
  lockTime?: TimeAsString;
  loanProduct: string;
  loanTerm: string;
  property: Address;
  appID: number;

  estimatedTotalAssetsRequired: DecimalAsString;
  estimatedClosingCosts: DecimalAsString;
  estimatedReservesRequired: DecimalAsString;
}

export const TransactionDetails: React.FunctionComponent<TransactionDetailsProps> = ({
  loanPurpose,
  occupancy,
  propertyType,
  closingDate,
  loanAmount,
  purchasePrice,
  LTV,
  interestRate,
  lockTime,
  loanProduct,
  loanTerm,
  property,
  appID,
  estimatedClosingCosts,
  estimatedTotalAssetsRequired,
  estimatedReservesRequired,
}) => (
  <Card className={styles.card}>
    <div className={styles.transactionHeader}>
      <h2> Transaction Details</h2>
      <Link to={`/underwriter/app/${appID}/LoanProductHurdle`}>View LoanProductHurdle</Link>
    </div>
    <table>
      <tbody>
        <DataRow title="Loan Purpose">{loanPurpose}</DataRow>
        <DataRow title="Occupancy">{occupancy}</DataRow>
        <DataRow title="Property Type">{propertyType}</DataRow>
        <DataRow title="COE Date">{closingDate || "None"}</DataRow>
        <DataRow title="Loan Amount">{<Money value={Number(loanAmount)} />}</DataRow>
        <DataRow title="Purchase Price">{<Money value={Number(purchasePrice)} />}</DataRow>
        <DataRow title="LTV">{formatRateAsPercent(LTV, 2)}</DataRow>
        <DataRow title="Interest Rate">{formatRateAsPercent(interestRate)}</DataRow>
        <DataRow title="Lock Date">
          {lockTime ? (
            <FormattedDate
              value={lockTime}
              year="numeric"
              month="numeric"
              day="numeric"
              hour="numeric"
              minute="numeric"
              second="numeric"
              timeZoneName="short"
              timeZone="America/Los_Angeles"
            />
          ) : (
            "Not Locked"
          )}
        </DataRow>
        <DataRow title="Loan Product">{loanProduct}</DataRow>
        <DataRow title="Loan Term">{loanTerm}</DataRow>
        <DataRow title="Property City">{property.CityName}</DataRow>
        <DataRow title="Property State">{property.StateCode}</DataRow>
        <DataRow title="Property County">{property.CountyName}</DataRow>
        <tr />
        <DataRow title="Estimated Total Assets Required">
          <Money value={Number(estimatedTotalAssetsRequired)} />
        </DataRow>
        <DataRow title="Estimated Downpayment">
          <Money value={Number(purchasePrice) - Number(loanAmount)} />
        </DataRow>
        <DataRow title="Estimated Closing Costs">
          <Money value={Number(estimatedClosingCosts)} />
        </DataRow>
        <DataRow title="Estimated Reserves Required">
          <Money value={Number(estimatedReservesRequired)} />
        </DataRow>
      </tbody>
    </table>
  </Card>
);

interface BorrowerQualificationProps {
  assetsTotal: DecimalAsString;
  monthlyDebtTotal: DecimalAsString;
  monthlyIncomeTotal: DecimalAsString;
  DTI: DecimalAsString;
  appID: number;
}

export const BorrowerQualification: React.FunctionComponent<BorrowerQualificationProps> = ({
  assetsTotal,
  monthlyDebtTotal,
  monthlyIncomeTotal,
  DTI,
  appID,
}) => (
  <Card className={styles.card}>
    <h2>Borrower Qualification Details</h2>
    <table>
      <tbody>
        <DataRow title="Assets Total">
          <Money value={Number(assetsTotal)} />
          <Link to={`/underwriter/app/${appID}/AssetSummaryHurdle`}>View AssetSummaryHurdle</Link>
        </DataRow>
        <DataRow title="Monthly Debt Total">
          <Money value={Number(monthlyDebtTotal)} />
          <Link to={`/underwriter/app/${appID}/CreditSummaryHurdle`}>View CreditSummaryHurdle</Link>
        </DataRow>
        <DataRow title="Monthly Income Total">
          <Money value={Number(monthlyIncomeTotal)} />
          <Link to={`/underwriter/app/${appID}/IncomeSummaryHurdle`}>View IncomeSummaryHurdle</Link>
        </DataRow>
        <DataRow title="DTI">{formatRateAsPercent(DTI, 2)}</DataRow>
      </tbody>
    </table>
  </Card>
);
